import React, { useState, useEffect } from "react";
import { X, PenLine, Check, ImagePlus, Trash2, Loader2, Type, Link2 } from "lucide-react";
import { toast } from "sonner";
import * as api from "../api";

const FONTS = ["Arial", "Georgia", "Times New Roman", "Verdana", "Trebuchet MS", "Courier New"];
const SIZES = [12, 14, 16, 18, 20, 24];

function ridimensiona(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 480;
        if (img.width <= maxW && reader.result.length < 400000) {
          resolve(reader.result);
          return;
        }
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function SignatureModal({ open, onClose, accounts, onSaved }) {
  const [accId, setAccId] = useState(null);
  const [testo, setTesto] = useState("");
  const [img, setImg] = useState("");
  const [font, setFont] = useState("Arial");
  const [size, setSize] = useState(14);
  const [link, setLink] = useState("");
  const [imgPos, setImgPos] = useState("sotto");
  const [imgLarg, setImgLarg] = useState(130);
  const [saving, setSaving] = useState(false);

  const acc = accounts.find((a) => a.id === accId) || null;

  useEffect(() => {
    if (open) {
      const first = accounts[0] || null;
      setAccId(first?.id || null);
      setTesto(first?.firma_testo || "");
      setImg(first?.firma_img || "");
      setFont(first?.firma_font || "Arial");
      setSize(first?.firma_size || 14);
      setLink(first?.firma_link || "");
      setImgPos(first?.firma_img_pos || "sotto");
      setImgLarg(first?.firma_img_larg || 130);
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scegliAccount = (a) => {
    setAccId(a.id);
    setTesto(a.firma_testo || "");
    setImg(a.firma_img || "");
    setFont(a.firma_font || "Arial");
    setSize(a.firma_size || 14);
    setLink(a.firma_link || "");
    setImgPos(a.firma_img_pos || "sotto");
    setImgLarg(a.firma_img_larg || 130);
  };

  const caricaImmagine = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    try {
      setImg(await ridimensiona(file));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error("Impossibile leggere l'immagine");
    }
  };

  const salva = async () => {
    if (!acc) return;
    setSaving(true);
    try {
      const linkPulito = link.trim() && !/^https?:\/\//i.test(link.trim()) ? "https://" + link.trim() : link.trim();
      await api.saveFirma(acc.id, testo, img, font, size, linkPulito, imgPos, imgLarg);
      onSaved(acc.id, { firma_testo: testo, firma_img: img, firma_font: font, firma_size: size, firma_link: linkPulito, firma_img_pos: imgPos, firma_img_larg: imgLarg });
      toast.success("Firma salvata per " + acc.email);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`wf-modal-backdrop ${open ? "show" : ""}`} onClick={onClose}>
      <div className="wf-modal" onClick={(ev) => ev.stopPropagation()} data-testid="signature-modal">
        <div className="wf-modal-head">
          <span className="wf-modal-title">
            <PenLine size={19} strokeWidth={2.3} /> Firme
          </span>
          <button className="icon-btn" onClick={onClose} aria-label="Chiudi" data-testid="signature-close">
            <X size={20} strokeWidth={2.6} />
          </button>
        </div>

        {accounts.length === 0 ? (
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink-soft)" }}>
            Collega prima un account di posta per impostare la firma.
          </div>
        ) : (
          <>
            <div className="firma-account-row">
              {accounts.map((a) => (
                <button
                  key={a.id}
                  className={`chip dd-item ${accId === a.id ? "active" : ""}`}
                  onClick={() => scegliAccount(a)}
                  data-testid={`signature-account-${a.id}`}
                >
                  {a.label}
                </button>
              ))}
            </div>

            <div className="wf-field">
              <label className="wf-label">Testo della firma</label>
              <div className="firma-toolbar" data-testid="signature-toolbar">
                <Type size={17} strokeWidth={2.4} style={{ flexShrink: 0, color: "var(--ink-soft)" }} />
                <select
                  className="firma-select"
                  value={font}
                  onChange={(e) => setFont(e.target.value)}
                  title="Font"
                  aria-label="Font"
                  data-testid="signature-font"
                >
                  {FONTS.map((f) => (
                    <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                  ))}
                </select>
                <select
                  className="firma-select firma-select-size"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                  title="Dimensione carattere"
                  aria-label="Dimensione carattere"
                  data-testid="signature-size"
                >
                  {SIZES.map((s) => (
                    <option key={s} value={s}>{`${s}px`}</option>
                  ))}
                </select>
                <label className="icon-btn firma-logo-btn" title="Aggiungi logo" aria-label="Aggiungi logo" data-testid="signature-logo-btn">
                  <ImagePlus size={19} strokeWidth={2.3} />
                  <input type="file" accept="image/*" onChange={caricaImmagine} style={{ display: "none" }} />
                </label>
              </div>
              <textarea
                className="wf-textarea"
                rows={4}
                placeholder={"Es.\nMario Rossi\nTel. 333 1234567"}
                value={testo}
                onChange={(e) => setTesto(e.target.value)}
                style={{ fontFamily: `'${font}', Arial, sans-serif`, fontSize: size }}
                data-testid="signature-text"
              />
            </div>

            {img && (
              <div className="wf-field">
                <label className="wf-label">Logo</label>
                <div className="firma-pos-row" data-testid="signature-image-pos">
                  <span className="wf-label" style={{ margin: 0 }}>Posizione</span>
                  {[
                    ["sopra", "Sopra il testo"],
                    ["sotto", "Sotto il testo"],
                    ["sinistra", "A sinistra"],
                    ["destra", "A destra"],
                  ].map(([v, l]) => (
                    <button key={v} type="button" className={`chip firma-pos-chip ${imgPos === v ? "active" : ""}`} onClick={() => setImgPos(v)} data-testid={`signature-pos-${v}`}>
                      {l}
                    </button>
                  ))}
                </div>
                <div className="firma-larg-row">
                  <span className="wf-label" style={{ margin: 0 }}>Grandezza</span>
                  <input type="range" min={60} max={480} step={10} value={imgLarg} onChange={(e) => setImgLarg(Number(e.target.value))} aria-label="Larghezza logo" data-testid="signature-image-size" />
                  <span className="firma-larg-val">{imgLarg} px</span>
                </div>
                <div className={`firma-anteprima pos-${imgPos}`} data-testid="signature-preview">
                  <div className="firma-anteprima-testo" style={{ fontFamily: `'${font}', Arial, sans-serif`, fontSize: size }}>{testo || "Testo della firma"}</div>
                  <img src={img} alt="Logo firma" className="firma-anteprima-img" style={{ width: imgLarg }} />
                </div>
                <div className="firma-img-box">
                  <img src={img} alt="Logo firma" className="firma-img" data-testid="signature-image" />
                  <button
                    className="icon-btn"
                    onClick={() => setImg("")}
                    aria-label="Rimuovi logo"
                    title="Rimuovi logo"
                    data-testid="signature-image-remove"
                  >
                    <Trash2 size={18} strokeWidth={2.4} />
                  </button>
                </div>
                <div className="firma-link-row">
                  <Link2 size={16} strokeWidth={2.4} style={{ flexShrink: 0, color: "var(--ink-soft)" }} />
                  <input
                    className="wf-input"
                    type="url"
                    placeholder="Sito web del logo, es. www.michelchef.it"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    data-testid="signature-link"
                  />
                </div>
                <div className="ai-note">Chi riceve la mail, toccando il logo, apre questo sito.</div>
              </div>
            )}

            <div className="wf-modal-actions">
              <button className="wf-btn primary" onClick={salva} disabled={saving || !acc} data-testid="signature-save">
                {saving ? (
                  <>
                    <Loader2 size={16} strokeWidth={2.6} className="spin-icon" /> Salvataggio...
                  </>
                ) : (
                  <>
                    <Check size={16} strokeWidth={2.6} /> Salva firma
                  </>
                )}
              </button>
            </div>

            <div className="ai-note">La firma viene aggiunta automaticamente in fondo alle email inviate da questo account.</div>
          </>
        )}
      </div>
    </div>
  );
}
